// @ts-check
// Zoom e centralização do canvas do fluxo (spec 2.0.0, fluxos.md seção 20.5, "Rolagem, zoom e centralização"; contrato
// 09 de fluxos, seção 17): níveis fixos, o ponto sob o ponteiro parado ao mudar o zoom com Ctrl ou Cmd e a roda, e a
// rolagem que deixa o cartão de entrada perto da esquerda com a linha principal no meio da altura. Funções puras; o
// zoom e o "Números" ficam guardados por pessoa no navegador (com try/catch, porque o armazenamento pode faltar).

export const FLOW_ZOOM_LEVELS = Object.freeze([0.5, 0.67, 0.8, 0.9, 1, 1.1, 1.25, 1.5]);
export const FLOW_DEFAULT_ZOOM = 1;
// no celular o canvas abre menor, para caber mais passos na tela
export const FLOW_MOBILE_ZOOM = 0.8;
export const FLOW_CENTER_LEFT_PIXELS = 64;
export const FLOW_ZOOM_STORAGE_PREFIX = 'appcrm.flowEditor.zoom.';
export const FLOW_SHOW_COUNTS_STORAGE_KEY = 'appcrm.flowEditor.showCounts';

/** Nível da lista mais próximo de um valor qualquer. @param {unknown} value */
export function normalizeFlowZoom(value) {
  const number = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(number)) return FLOW_DEFAULT_ZOOM;
  return FLOW_ZOOM_LEVELS.reduce((best, level) => (Math.abs(level - number) < Math.abs(best - number) ? level : best), FLOW_ZOOM_LEVELS[0]);
}

/** Próximo nível acima (o mesmo no máximo). @param {number} zoom */
export function zoomInLevel(zoom) {
  const current = normalizeFlowZoom(zoom);
  return FLOW_ZOOM_LEVELS.find((level) => level > current) ?? current;
}

/** Próximo nível abaixo (o mesmo no mínimo). @param {number} zoom */
export function zoomOutLevel(zoom) {
  const current = normalizeFlowZoom(zoom);
  return [...FLOW_ZOOM_LEVELS].reverse().find((level) => level < current) ?? current;
}

/** @param {number} zoom */
export const canZoomIn = (zoom) => normalizeFlowZoom(zoom) < FLOW_ZOOM_LEVELS[FLOW_ZOOM_LEVELS.length - 1];
/** @param {number} zoom */
export const canZoomOut = (zoom) => normalizeFlowZoom(zoom) > FLOW_ZOOM_LEVELS[0];

/** "100%". @param {number} zoom */
export const flowZoomPercent = (zoom) => `${Math.round(normalizeFlowZoom(zoom) * 100)}%`;

/**
 * Rolagem nova para o ponto do conteúdo sob o ponteiro continuar sob ele depois do zoom.
 * @param {{ scrollLeft: number, scrollTop: number, pointerX: number, pointerY: number, from: number, to: number }} input
 *   pointerX e pointerY: posição do ponteiro dentro da área visível
 * @returns {{ scrollLeft: number, scrollTop: number }}
 */
export function scrollKeepingPoint({ scrollLeft, scrollTop, pointerX, pointerY, from, to }) {
  const ratio = to / from;
  return {
    scrollLeft: Math.max(0, (scrollLeft + pointerX) * ratio - pointerX),
    scrollTop: Math.max(0, (scrollTop + pointerY) * ratio - pointerY),
  };
}

/**
 * Rolagem de "Centralizar": o cartão de entrada a 64 px da esquerda e o meio dele no meio da altura visível.
 * @param {{ entryLeft: number, entryTop: number, entryHeight: number, viewportHeight: number, zoom: number }} input
 *   entryLeft, entryTop e entryHeight em pixels do conteúdo sem zoom
 */
export function centeredScroll({ entryLeft, entryTop, entryHeight, viewportHeight, zoom }) {
  return {
    scrollLeft: Math.max(0, entryLeft * zoom - FLOW_CENTER_LEFT_PIXELS),
    scrollTop: Math.max(0, (entryTop + entryHeight / 2) * zoom - viewportHeight / 2),
  };
}

/** @param {string} key */
function readStored(key) {
  try {
    return globalThis.localStorage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

/** @param {string} key @param {string} value */
function writeStored(key, value) {
  try {
    globalThis.localStorage?.setItem(key, value);
  } catch {
    // sem armazenamento (navegação privada, cota cheia): o editor segue com o padrão
  }
}

/** Zoom guardado para o fluxo, ou o padrão. @param {string} flowId @param {number} [fallback] */
export function readStoredFlowZoom(flowId, fallback = FLOW_DEFAULT_ZOOM) {
  const stored = readStored(`${FLOW_ZOOM_STORAGE_PREFIX}${flowId}`);
  return stored === null ? fallback : normalizeFlowZoom(Number(stored));
}

/** @param {string} flowId @param {number} zoom */
export const storeFlowZoom = (flowId, zoom) => writeStored(`${FLOW_ZOOM_STORAGE_PREFIX}${flowId}`, String(normalizeFlowZoom(zoom)));

/** "Números" ligado? Padrão: ligado quando o fluxo já foi ativado. @param {boolean} fallback */
export function readStoredShowCounts(fallback) {
  const stored = readStored(FLOW_SHOW_COUNTS_STORAGE_KEY);
  return stored === null ? fallback : stored === 'true';
}

/** @param {boolean} value */
export const storeShowCounts = (value) => writeStored(FLOW_SHOW_COUNTS_STORAGE_KEY, value ? 'true' : 'false');
